import debounce from 'lodash/debounce';

const allImages = document.querySelectorAll('.twoyou-responsive-image-container picture');

const addWrappingAnchorTag = (link, img, rel, target, classes) => {
  // Create the a tag element and get parent wrapper.
  const aTag = document.createElement('a');
  const parentWrapper = img.closest('.twoyou-responsive-image-container');

  // Add the href to the a new tag element.
  aTag.href = link;
  // Add link targets.
  if (target) {
    aTag.setAttribute('target', target);
    aTag.setAttribute('rel', rel);
  }
  // Add classes.
  if (classes) {
    aTag.setAttribute('class', classes);
  }
  // Append the img to the aTag.
  aTag.appendChild(img);
  // replace all the markup from the picture tag and replace it with the new markup that includes the wrapping a tag
  parentWrapper.appendChild(aTag);
};

const removeWrappingAnchorTag = img => {
  // Check there is an a tag to delete.
  if (img.parentElement.nodeName === 'A') {
    // Delete it.
    img.parentElement.replaceWith(...img.parentElement.childNodes);
  }
};

const addFigCaption = img => {
  if (img.nextElementSibling?.nodeName !== 'FIGCAPTION') {
    const figCaptionEl = document.createElement('figcaption');
    img.after(figCaptionEl);
  }
};

const removeFigCaption = img => {
  if (img.nextElementSibling?.nodeName === 'FIGCAPTION') {
    img.nextElementSibling.remove();
  }
};

Array.from(allImages).forEach(image => {
  const allDataAttrs = JSON.parse(image.dataset.json);

  // remove any null values from the json data
  Object.keys(allDataAttrs).forEach(key => {
    if (allDataAttrs[key] === null) {
      delete allDataAttrs[key];
    }
  });

  // build up the variables necessary for the link tag
  const desktopHref =
    typeof allDataAttrs?.desktop?.href === 'undefined' ? '' : allDataAttrs.desktop.href;
  let tabletHref =
    typeof allDataAttrs?.tablet?.href === 'undefined' ? '' : allDataAttrs.tablet.href;
  let mobileHref =
    typeof allDataAttrs?.mobile?.href === 'undefined' ? '' : allDataAttrs.mobile.href;

  // build up the variables necessary for the alt tag
  const desktopAlt =
    typeof allDataAttrs?.desktop?.alt === 'undefined' ? '' : allDataAttrs.desktop.alt;
  const tabletAlt = typeof allDataAttrs?.tablet?.alt === 'undefined' ? '' : allDataAttrs.tablet.alt;
  const mobileAlt = typeof allDataAttrs?.mobile?.alt === 'undefined' ? '' : allDataAttrs.mobile.alt;

  // build up the variables necessary for the target
  const desktopTarget =
    typeof allDataAttrs?.desktop?.target === 'undefined' ? '' : allDataAttrs.desktop.target;
  let tabletTarget =
    typeof allDataAttrs?.tablet?.target === 'undefined' ? '' : allDataAttrs.tablet.target;
  let mobileTarget =
    typeof allDataAttrs?.mobile?.target === 'undefined' ? '' : allDataAttrs.mobile.target;

  // build up the variables necessary for the rel tag
  const desktopRel =
    typeof allDataAttrs?.desktop?.rel === 'undefined' ? '' : allDataAttrs.desktop.rel;
  const tabletRel = typeof allDataAttrs?.tablet?.rel === 'undefined' ? '' : allDataAttrs.tablet.rel;
  const mobileRel = typeof allDataAttrs?.mobile?.rel === 'undefined' ? '' : allDataAttrs.mobile.rel;

  const imageClasses = Array.from(image.closest('picture').querySelectorAll('source')).map(source =>
    source.className.replace('twoyou-responsive-image--', ''),
  );

  // build up the variables necessary for the caption
  const desktopCaption =
    typeof allDataAttrs?.desktop?.caption === 'undefined' ? '' : allDataAttrs.desktop.caption;
  const tabletCaption =
    typeof allDataAttrs?.tablet?.caption === 'undefined' ? '' : allDataAttrs.tablet.caption;
  const mobileCaption =
    typeof allDataAttrs?.mobile?.caption === 'undefined' ? '' : allDataAttrs.mobile.caption;

  const handleImageAltTags = debounce(() => {
    const windowWidth = window.innerWidth;
    if (windowWidth < 760) {
      const mobileSet = imageClasses.includes('mobile');
      image.lastElementChild.setAttribute('alt', mobileAlt);
      if (!mobileSet && !mobileHref.length && tabletHref) {
        mobileHref = tabletHref;
        mobileTarget = tabletTarget;
      }
      if (mobileHref) {
        addWrappingAnchorTag(mobileHref, image, mobileRel, mobileTarget, allDataAttrs.mobile.class);
      } else {
        removeWrappingAnchorTag(image);
      }
    }
    if (windowWidth >= 760 && windowWidth < 1024) {
      const tabletSet = imageClasses.includes('tablet');
      image.lastElementChild.setAttribute('alt', tabletAlt);
      if (!tabletSet && !tabletHref.length && desktopHref) {
        tabletHref = desktopHref;
        tabletTarget = desktopTarget;
      }
      if (tabletHref) {
        addWrappingAnchorTag(tabletHref, image, tabletRel, tabletTarget, allDataAttrs.tablet.class);
      } else {
        removeWrappingAnchorTag(image);
      }
    }

    if (windowWidth >= 1024) {
      image.lastElementChild.setAttribute('alt', desktopAlt);
      if (desktopHref) {
        addWrappingAnchorTag(
          desktopHref,
          image,
          desktopRel,
          desktopTarget,
          allDataAttrs.desktop.class,
        );
      } else {
        removeWrappingAnchorTag(image);
      }
    }
  }, 250);

  const handleImageCaptions = debounce(() => {
    const windowWidth = window.innerWidth;
    if (windowWidth < 760) {
      if (mobileCaption) {
        addFigCaption(image);
        image.nextElementSibling.innerHTML = mobileCaption; // eslint-disable-line no-param-reassign
      } else {
        removeFigCaption(image);
      }
    }
    if (windowWidth >= 760 && windowWidth < 1024) {
      if (tabletCaption) {
        addFigCaption(image);
        image.nextElementSibling.innerHTML = tabletCaption; // eslint-disable-line no-param-reassign
      } else {
        removeFigCaption(image);
      }
    }
    if (windowWidth >= 1024) {
      if (desktopCaption) {
        addFigCaption(image);
        image.nextElementSibling.innerHTML = desktopCaption; // eslint-disable-line no-param-reassign
      } else {
        removeFigCaption(image);
      }
    }
  }, 250);

  if (image.closest('.twoyou-responsive-image-container')) {
    if (document.readyState !== 'loading') {
      handleImageAltTags();
      handleImageCaptions();
    } else {
      window.addEventListener('DOMContentLoaded', handleImageAltTags);
      window.addEventListener('DOMContentLoaded', handleImageCaptions);
    }
    window.addEventListener('resize', handleImageAltTags, false);
    window.addEventListener('resize', handleImageCaptions, false);
  }
});
